import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Spinner, Tabs, Tab } from 'react-bootstrap';
import Filter from './components/filter/filter';
import CorporateActionPaymentReportContainer from './components/corporate-action-payment-report-container/corporate-action-payment-report-container';
import IssuerAdhocReportContainer from './components/issuer-adhoc-report-container/issuer-adhoc-report-container';
import EventBasedReportContainer from './components/event-based-report-container/event-based-report-container';
import { useHistory } from 'react-router-dom';
import ReportsService from '../../../../services/reports-service';

import './ca-maker-reports-module.scss';

/**
 * CaMakerReportsModule is the main container component for handling and displaying
 * different types of reports. It fetches data, handles tab switching, and passes
 * filtered results to the appropriate report container components.
 */
const CaMakerReportsModule = () => {
  // Track active tab: default is "corporate"
  const [activeTab, setActiveTab] = useState('corporate');
  const [loading, setLoading] = useState(true);

  // State variables for each report type
  const [interestItems, setInterestItems] = useState([]);
  const [filteredInterestItems, setFilteredInterestItems] = useState([]);
  const [eventItems, setEventItems] = useState([]);
  const [filteredEventItems, setFilteredEventItems] = useState([]);
  const [issuerItems, setIssuerItems] = useState([]);
  const [filteredIssuerItems, setFilteredIssuerItems] = useState([]);
  const [selectedReports, setSelectedReports] = useState(new Set());

  // Create a memoized instance of ReportsService for API calls.
  const reportsService = useMemo(() => new ReportsService(), []);
  const history = useHistory();

  // Fetch reports data when the component mounts.
  useEffect(() => {
    const getReports = async () => {
      try {
        // API call to get securities data
        const result = await reportsService.getSecurities();
        // Set Interest Based Reports data
        setInterestItems(result.items);
        setFilteredInterestItems(result.items);

        // Set Issuer-Adhoc Reports data from item2
        setIssuerItems(result.item2);
        setFilteredIssuerItems(result.item2);

        // Placeholder for Event-Based Reports (to be implemented later)
        setEventItems(result.item3);
        setFilteredEventItems(result.item3);

        // console.log('Reports fetched:', result);
      } catch (error) {
        console.error('Error fetching reports:', error);
        history.push('/internal-server-error');
      } finally {
        setLoading(false);
      }
    };
    getReports();
  }, [history, reportsService]);

  /**
   * handleFilterChange applies filtering logic based on the active report tab.
   * @param {Object} filters - Filter criteria selected by the user.
   */
  const handleFilterChange = useCallback(
    (filters) => {
      if (activeTab === 'corporate') {
        const { status, paymentDate, issuers } = filters;
        const filtered = interestItems.filter((item) => {
          const matchesStatus = status.length ? status.includes(item.status) : true;
          const matchesPaymentDate = paymentDate ? item.paymentDate === paymentDate : true;
          const matchesIssuer = issuers.length ? issuers.includes(item.issuer) : true;
          return matchesStatus && matchesPaymentDate && matchesIssuer;
        });
        setFilteredInterestItems(filtered);
      } else if (activeTab === 'event') {
        const { status } = filters; // Example filter key
        const filtered = eventItems.filter((item) => {
          const matchesStatus = status.length ? status.includes(item.status) : true;
          return matchesStatus;
        });
        setFilteredEventItems(filtered);
      } else if (activeTab === 'issuer') {
        const { status } = filters; // Example filter key
        const filtered = issuerItems.filter((item) => {
          const matchesStatus = status.length ? status.includes(item.status) : true;
          return matchesStatus;
        });
        setFilteredIssuerItems(filtered);
      }
    },
    [activeTab, interestItems, eventItems, issuerItems]
  );

  const handleCheckboxChange = (id) => {
    setSelectedReports((prev) => {
      const newSelected = new Set(prev);
      newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id);
      return newSelected;
    });
  };


  // Determine which set of items is used for filtering based on the active tab.
  const filterItems =
    activeTab === 'corporate' ? interestItems : activeTab === 'event' ? eventItems : issuerItems;

  return (
    <Container fluid className="maker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <Tabs
            activeKey={activeTab}
            onSelect={(tab) => setActiveTab(tab)}
            className="mb-3"
          >
            <Tab eventKey="corporate" title="Corporate Action Payment Reports" />
            <Tab eventKey="event" title="Event-Based Reports" />
            <Tab eventKey="issuer" title="Issuer-Adhoc Reports" />
          </Tabs>
          <Row>
            {/* Render Filter only when in Interest Based Reports tab */}
            {activeTab === 'corporate' && (
              <Col xs={12} md={3} className="panel">
                <Filter
                  items={filterItems}
                  filter={{ status: [], paymentDate: '', issuers: [] }}
                  onFilterChange={handleFilterChange}
                />
              </Col>
            )}
            <Col xs={12} md={activeTab === 'corporate' ? 9 : 12}>
              {activeTab === 'corporate' && (
                <CorporateActionPaymentReportContainer items={filteredInterestItems} />
              )}
              {activeTab === 'event' && (
                <EventBasedReportContainer items={filteredEventItems} handleCheckboxChange={handleCheckboxChange} />
              )}
              {activeTab === 'issuer' && (
                <IssuerAdhocReportContainer items={filteredIssuerItems} />
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default CaMakerReportsModule;
